import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Section, GridContainer, DividerDashed } from "../layout";
import { AnimatedSection } from "../shared";
import { PillarLabel } from "../ui";
import { InvestmentKanbanWidget, InvestmentCreationWidget, PromotersCRMWidget } from "../widgets";

const TICK_INTERVAL = 50; // Update every 50ms

const features = [
  {
    id: "crm",
    title: "CRM unificado",
    description:
      "Promotores e inversores en un solo lugar. Historial de inversiones, documentos y comunicaciones al alcance.",
    Widget: PromotersCRMWidget,
    duration: 10000, // 10 seconds
  },
  {
    id: "crear",
    title: "Crea Inversiones",
    description:
      "Selecciona promotor, cuota fiscal e inversor. El sistema calcula automáticamente la capacidad disponible.",
    Widget: InvestmentCreationWidget,
    duration: 18000, // 18 seconds (longer animation)
  },
  {
    id: "kanban",
    title: "Pipeline Kanban",
    description:
      "Visualiza cada inversión en su estado exacto. Arrastra entre columnas para avanzar el estado del proceso.",
    Widget: InvestmentKanbanWidget,
    duration: 10000, // 10 seconds
  },
];

// Widget dimensions and mobile scale factors
const widgetConfig: Record<
  string,
  { width: number; height: number; mobileScale: number; mobileOffset: string }
> = {
  crm: { width: 620, height: 350, mobileScale: 0.65, mobileOffset: "5%" },
  crear: { width: 500, height: 370, mobileScale: 0.7, mobileOffset: "10%" },
  kanban: { width: 920, height: 500, mobileScale: 0.62, mobileOffset: "15%" },
};

/**
 * Pillar 1: Gestiona - Pipeline management features.
 * Features auto-rotate with a vertical progress indicator.
 */
export function PillarGestiona() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);

  const goToFeature = (index: number) => {
    setActiveIndex(index);
    setProgress(0);
    progressRef.current = 0;
  };

  // Auto-rotation timer (each feature has its own duration)
  useEffect(() => {
    if (isPaused) return;

    progressRef.current = 0;

    const currentDuration = features[activeIndex].duration;

    const interval = setInterval(() => {
      progressRef.current += 100 / (currentDuration / TICK_INTERVAL);

      if (progressRef.current >= 100) {
        setActiveIndex((prev) => (prev + 1) % features.length);
        progressRef.current = 0;
      }
      setProgress(progressRef.current);
    }, TICK_INTERVAL);

    return () => clearInterval(interval);
  }, [isPaused, activeIndex]);

  const ActiveWidget = features[activeIndex].Widget;

  return (
    <Section id="producto" size="md" className="relative z-10">
      <AnimatedSection>
        <GridContainer>
          {/* Pillar Label */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12">
            <PillarLabel number="01" label="Gestiona" />
          </div>

          {/* Header wrapped with dashed lines (touching) */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12 mt-4">
            <DividerDashed />
            <h2 className="text-2xl md:text-3xl xl:text-4xl font-semibold text-text-primary tracking-tight md:max-w-[66%]">
              Un solo pipeline estructurado.
            </h2>
            <p className="text-2xl md:text-3xl xl:text-4xl font-semibold text-text-subtitle tracking-tight md:max-w-[66%]">
              <span className="md:hidden">De prospecto a declaración.</span>
              <span className="hidden md:inline">De prospecto a declaración fiscal.</span>
            </p>
            <DividerDashed />
          </div>

          {/* Dashed line before tabs (16px below title) */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12 mt-4">
            <DividerDashed />
          </div>
        </GridContainer>

        {/* ===== MOBILE/TABLET (<1024px) ===== */}
        <div className="lg:hidden max-w-[1440px] mx-auto px-6 md:px-8">
          {/* Horizontal tabs with dashed line */}
          <div className="relative">
            {/* Dashed line - behind everything, full screen width */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-screen">
              <DividerDashed />
            </div>

            {/* Tabs row */}
            <div className="relative flex gap-3 md:gap-6">
              {features.map((feature, index) => {
                const isActive = index === activeIndex;

                return (
                  <button
                    key={feature.id}
                    onClick={() => goToFeature(index)}
                    className={`relative flex-shrink-0 py-3 pr-1 text-left touch-manipulation whitespace-nowrap ${index === 0 ? "pl-0" : "pl-1"}`}
                  >
                    <span
                      className={`text-body-small font-medium transition-colors duration-200 ${
                        isActive ? "text-text-tertiary" : "text-text-tertiary"
                      }`}
                    >
                      {feature.title}
                    </span>

                    {/* Progress bar - sits on top of dashed line */}
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
                      {isActive && (
                        <motion.div
                          className="h-full bg-text-primary"
                          initial={{ width: "0%" }}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.05, ease: "linear" }}
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Descriptions – grid overlap so container always matches the tallest */}
          <div className="grid mt-4 mb-6 max-w-[75%]">
            {features.map((feature, index) => (
              <p
                key={feature.id}
                className={`text-body-small text-text-secondary col-start-1 row-start-1 transition-opacity duration-200 ${
                  index === activeIndex ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                aria-hidden={index !== activeIndex}
              >
                {feature.description}
              </p>
            ))}
          </div>

          {/* Widget - centrado y ligeramente a la derecha */}
          <div className="relative overflow-visible -mr-6 md:-mr-8" style={{ height: 300 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex justify-center"
                style={{
                  transform: `scale(${widgetConfig[features[activeIndex].id].mobileScale})`,
                  transformOrigin: "top center",
                  marginLeft: widgetConfig[features[activeIndex].id].mobileOffset,
                }}
              >
                <ActiveWidget autoPlay />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* ===== DESKTOP (>=1024px) ===== */}
        <div className="hidden lg:block max-w-[1440px] mx-auto px-6 md:px-8 xl:pl-[180px] xl:pr-0 overflow-hidden -mt-px">
          <div
            className="grid grid-cols-1 md:grid-cols-8 xl:grid-cols-12 gap-8 min-h-[400px] md:min-h-[450px] xl:min-h-[500px]"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Left: Feature Selector (aligned to top) */}
            <div className="md:col-span-3 xl:col-span-4 flex flex-col justify-start gap-2">
              {features.map((feature, index) => {
                const isActive = index === activeIndex;

                return (
                  <button
                    key={feature.id}
                    onClick={() => goToFeature(index)}
                    className={`relative text-left pl-4 py-3 transition-colors duration-200 ${
                      isActive ? "cursor-default" : "cursor-pointer hover:bg-tertiary/50"
                    }`}
                  >
                    {/* Progress Line (left border) */}
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-border-secondary overflow-hidden">
                      {isActive && (
                        <motion.div
                          className="absolute left-0 top-0 w-full bg-text-primary"
                          initial={{ height: "0%" }}
                          animate={{ height: `${progress}%` }}
                          transition={{ duration: 0.05, ease: "linear" }}
                        />
                      )}
                    </div>

                    {/* Title */}
                    <h3
                      className={`text-h3 transition-colors duration-200 ${
                        isActive ? "text-text-tertiary" : "text-text-tertiary"
                      }`}
                    >
                      {feature.title}
                    </h3>

                    {/* Description (always visible, color changes based on active state) */}
                    <p
                      className={`text-body-small mt-2 transition-colors duration-200 ${
                        isActive ? "text-text-secondary" : "text-text-tertiary"
                      }`}
                    >
                      {feature.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Right: Active Widget (starts at column 5, aligned to top) */}
            <div className="md:col-span-5 xl:col-start-5 xl:col-span-8 h-full flex items-start md:-mr-16 xl:-mr-32">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <ActiveWidget autoPlay />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </Section>
  );
}
